import React from "react";
//Limitless imports
import { useTranslation, useResources } from "@limitless/common/hooks";
import { useWebSockets } from "@limitless/common/hooks/web-sockets/WebSocketsContext";
import { ClientContext } from "@limitless/common/utils";
import { useCustomerMessageContext, useCustomerContext, INITIAL_CUSTOMER_STATE } from "@limitless/customer/hooks";
import { validateCustomer } from "@limitless/customer/hooks/customer/CustomerValidator";
import { createExpertReachOut, createStaticReachOut } from "utils/LLMWindowObject";
import { useAppVisibilityContext } from "hooks/app-visibility/AppVisibilityContext";

const INITIAL_REACT_OUT_MESSAGE = {
    reachOutDialogue: null,
    messageId: null,
    accessKey: null,
    showCoachingCards: false,
    includeExpertBio: false
};

/**
 * Reach out experience
 */
export const useReachOutExperience = () => {
    const [isReachOutMode, setIsReachOutMode] = React.useState(false);
    const [reachOutMessage, setReachOutMessage] = React.useState(INITIAL_REACT_OUT_MESSAGE);
    const [originalFabConfig] = React.useState({
        expandable: ClientContext._expandableFab,
        fabText: ClientContext._fabText,
        mobileText: ClientContext._fabMobileText
    });

    const { createMessage, message } = useCustomerMessageContext();
    const {
        updatePostHiwAction,
        replaceAndSaveCustomer,
        updateAutoSubmit,
        updateHIWViewedStatus,
        clearQuestion
    } = useCustomerContext();
    const { t } = useTranslation();
    const {
        appVisibility: { isAppVisible },
        toggleAppOpen,
        toggleAppVisibility
    } = useAppVisibilityContext();
    const { resources, updateResourceValue } = useResources();
    const { connectToWebSocket } = useWebSockets();

    React.useEffect(() => {
        createExpertReachOut(params => handleExpertReachOut(params));
        createStaticReachOut(params => handleStaticReachOut(params, resources.staticTextEventMapping));
        //recreate the object each time a dep changes to avoid stale data in handleExpertReachOut/handleStaticReachOut
    }, [isAppVisible, reachOutMessage, resources]);

    React.useEffect(() => {
        //only process reach out experience if the app isn't open
        if (!isAppVisible) {
            sanitiseReachOutMessage(message);
        }
    }, [message]);

    const validateReachOutParams = (params, targetMode) => {
        if (!params || typeof params !== "object") {
            return "Params cannot be null and must be an object";
        }

        if (isAppVisible) {
            return "Cannot create reach out experience while LLM is visible";
        }

        if (reachOutMessage.messageId !== null || reachOutMessage.reachOutDialogue) {
            return "Reach out message already created.";
        }

        if (ClientContext._reachOutMode !== targetMode) {
            return `data-reach-out-mode must be configured to ”${targetMode}” to create a ${targetMode} experience`;
        }

        return null;
    };

    const validateReachOutOptions = params => {
        const { email = "", name = "", question = "", extraInfo, showCoachingCards, includeExpertBio } = params;

        const customerToValidate = { ...INITIAL_CUSTOMER_STATE };
        customerToValidate.name.value = name;
        customerToValidate.name.pristine = false;
        customerToValidate.email.value = email;
        customerToValidate.email.pristine = false;
        customerToValidate.question.value = question;
        customerToValidate.question.pristine = false;

        ClientContext._extraInfo = extraInfo;

        const { isFormInvalid, fieldErrors } = validateCustomer(customerToValidate, t, false);

        if (!isFormInvalid) {
            //save the customer if it's valid
            replaceAndSaveCustomer({ ...customerToValidate, isFormInvalid, fieldErrors });
            //disable expandable fab
            ClientContext._expandableFab = false;
            ClientContext._fabMobileText = "";
            ClientContext._fabText = "";
        }

        if (showCoachingCards && typeof showCoachingCards !== "boolean") {
            fieldErrors.showCoachingCards = "showCoachingCards must be a boolean";

            return { fieldErrors, isFormInvalid: true };
        }

        if (showCoachingCards && ClientContext._hiwMode === "disabled") {
            fieldErrors.showCoachingCards = "data-how-it-works must not be disabled if showCoachingCards is true";

            return { fieldErrors, isFormInvalid: true };
        }

        if (includeExpertBio && typeof includeExpertBio !== "boolean") {
            fieldErrors.includeExpertBio = "includeExpertBio must be a boolean";

            return { fieldErrors, isFormInvalid: true };
        }

        return { isFormInvalid, fieldErrors };
    };

    const handleExpertReachOut = params => {
        return new Promise((resolve, reject) => {
            const error = validateReachOutParams(params, "expert");

            if (error) {
                reject({ error });

                return;
            }

            const { isFormInvalid, fieldErrors } = validateReachOutOptions(params);

            if (isFormInvalid) {
                reject({ fieldErrors });

                return;
            }

            connectToWebSocket();

            //this have been validated above.
            const { email, name, question, showCoachingCards = false, includeExpertBio = false } = params;

            createMessage({ email, name, question }, newMessage => {
                if (newMessage) {
                    setReachOutMessage({
                        messageId: newMessage.messageId,
                        accessKey: newMessage.accessKey,
                        reachOutDialogue: null,
                        showCoachingCards,
                        includeExpertBio
                    });
                    resolve(newMessage);
                } else {
                    reject({ error: "Failed to create message, contact Limitless for support" });
                }
            });
        });
    };

    const handleStaticReachOut = (params, staticTextEventMapping) => {
        return new Promise((resolve, reject) => {
            const error = validateReachOutParams(params, "static");

            if (error) {
                reject({ error });

                return;
            }

            const { eventKey, staticText } = params;

            if (!params.eventKey) {
                reject({
                    fieldErrors: {
                        eventKey: "eventKey is mandatory"
                    }
                });

                return;
            }
            //event key is used as a mapping, but all used as the question submitted to the platform
            const paramsWithQuestion = { ...params, question: eventKey };

            const { isFormInvalid, fieldErrors } = validateReachOutOptions(paramsWithQuestion);

            if (isFormInvalid) {
                if (fieldErrors["question"]) {
                    fieldErrors.eventKey = fieldErrors["question"];
                    Reflect.deleteProperty(fieldErrors, "question");
                }
                reject({ fieldErrors });

                return;
            }

            const defaultStaticText = t("Would you like to chat with an Expert?");
            //try to find a mapping from the remote resource
            const eventKeyLookupText = staticTextEventMapping.find(mapping => mapping.eventKey === eventKey);

            const staticBody = staticText ? staticText : eventKeyLookupText?.staticText ?? defaultStaticText;

            const staticDialogue = {
                body: staticBody
            };
            const { showCoachingCards = false } = params;
            setIsReachOutMode(true);
            setReachOutMessage({
                ...reachOutMessage,
                reachOutDialogue: staticDialogue,
                showCoachingCards
            });
            resolve({ success: "Static reach out successfully created." });
        });
    };

    const sanitiseReachOutMessage = message => {
        const { dialogues } = message;
        const expertDialogue = dialogues.find(d => d.origin === "expert");

        if (expertDialogue) {
            let body;
            const { author, userId } = expertDialogue;

            if (expertDialogue.type === "suggestedAnswer") {
                body = expertDialogue.suggestedAnswers[0]?.body;
            } else {
                body = expertDialogue.body;
            }

            const reachOutDialogue = {
                author,
                body,
                userId
            };
            setIsReachOutMode(true);
            setReachOutMessage({ ...reachOutMessage, reachOutDialogue });
            const expert = message.expertAuthors[userId];

            if (expert && reachOutMessage.includeExpertBio) {
                const { rating, answerCount, thumbnail } = expert;
                const { hiwSlides } = resources;
                //only add the bio card once
                if (!hiwSlides[hiwSlides.length - 1].bioCard) {
                    const newSlides = [...hiwSlides];

                    const imgSrc = thumbnail ? `data:image/png;base64,${thumbnail}` : hiwSlides[0];

                    newSlides.push({
                        id: hiwSlides.length + 1,
                        imgSrc,
                        title: t("Expert Help"),
                        headline: t(
                            "{0} is ready to help with {1}% rating and over {2} customers helped",
                            author,
                            rating,
                            answerCount
                        ),
                        buttonText: t("Get Started"),
                        primaryColour: hiwSlides[0].primaryColour,
                        contrastColour: hiwSlides[0].contrastColour,
                        bioCard: true,
                        roundImage: thumbnail ? true : false
                    });

                    updateResourceValue("hiwSlides", newSlides);
                }
            }
        }
    };

    const handleReachOutClick = fabClickHandle => {
        clearReachOutMode();

        if (ClientContext._reachOutMode === "static") {
            //we haven't created the message yet.
            //if we set the customer autoSubmit prop, it will submit and open LLM
            if (!reachOutMessage.showCoachingCards) {
                updateAutoSubmit(true);
                updateHIWViewedStatus(true);
            } else {
                //we want to see the coaching screens
                //open LLM and set the post HIW action to be auto submit so the question is submitted after
                updatePostHiwAction("auto-submit");
            }

            fabClickHandle();
        } else {
            if (!reachOutMessage.showCoachingCards) {
                //message already submitted, open LLM
                fabClickHandle();
            } else {
                //we want to see the coaching screens
                //open LLM and set the post HIW action to be the conversation
                toggleAppOpen();
                updatePostHiwAction("conversation");
            }
        }
    };

    const handleCloseReachOut = () => {
        clearReachOutMode();
        toggleAppVisibility(true);
        clearQuestion();
    };

    const clearReachOutMode = () => {
        setIsReachOutMode(false);
        //we override values from Client Context as Reachout is a hybrid open/close experience
        //once LLM is open, it's back to BAU
        const { expandable, fabText, mobileText } = originalFabConfig;
        ClientContext._expandableFab = expandable;
        ClientContext._fabText = fabText;
        ClientContext._fabMobileText = mobileText;
    };

    return {
        isReachOutMode,
        reachOutMessage,
        handleReachOutClick,
        handleCloseReachOut
    };
};
